import { formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/settings/audit-log.tsx
 * @description 操作日誌管理頁面（Task 73）。
 *
 * 使用已存在的 /api/audit-log 路由（Task 31）。
 * 功能：
 * - 唯讀列表（actorId / action / entityType / entityId / createdAt / message）
 * - 編輯備注（只能修改 message 欄位，需 audit-log.write 能力）
 * - 軟刪除（需 audit-log.delete 能力，僅 admin）
 *
 * 視覺：ListPageShell + FilterBar + StatusPill + DataFieldGrid（詳情 Modal）
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, DatePicker, Form, Input, Modal, Select, Space, Table, Tooltip } from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import {
  Pencil,
  Trash2,
  Eye,
  ScrollText,
  Download,
  Activity,
  CalendarDays,
  LogIn,
  TriangleAlert,
  Search,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';
import { formatAuditAction, auditEntityLabel, AUDIT_METHOD_OPTIONS } from '@/lib/audit-log-i18n';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  ListPageShell,
  PageMetaItem,
  FilterBar,
  FilterBarLabel,
  StatusPill,
  DataField,
  DataFieldGrid,
} from '@/components/shell';
import { color, font, space, text, weight } from '@/styles/tokens';

const { RangePicker } = DatePicker;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/audit-log')({ component: AuditLogPage });

/**
 * 操作日誌實體類型（對應後端 auditLog 表）。
 */
interface AuditLogEntry {
  id: number;
  actorId: number | null;
  /** 操作人姓名（序117；後端 JOIN users 帶出，帳號已刪除為 null） */
  actorName: string | null;
  actorRole: string | null;
  action: string;
  entityType: string;
  entityId: number | null;
  ip: string | null;
  message: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
}

/**
 * 後端分頁響應結構。
 */
interface AuditLogListResponse {
  items: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 操作日誌管理頁面 */
function AuditLogPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles.includes('admin');

  const [searchText, setSearchText] = useState('');
  // 動作分類篩選：值為 HTTP 方法（POST/PATCH/DELETE/GET），由後端做 action 前綴匹配
  const [filterMethod, setFilterMethod] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  // 序234：真正的服務器端分頁狀態（不能只靠後端回傳、前端本地截斷）
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  /** 篩選條件變化時把頁碼重置回 1，否則在第 2 頁搜尋會看到空結果 */
  const resetToFirstPage = () => setPage(1);

  /** 查詢操作日誌列表 */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-audit-log', searchText, filterMethod, dateRange, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (searchText) params.set('search', searchText);
      if (filterMethod) params.set('method', filterMethod);
      if (dateRange?.[0]) params.set('from', dateRange[0].format('YYYY-MM-DD'));
      if (dateRange?.[1]) params.set('to', dateRange[1].format('YYYY-MM-DD'));
      return apiClient.get<AuditLogListResponse>(`/api/audit-log?${params.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // 口徑說明：以下兩個統計卡僅基於「當前頁」資料計算（非後端聚合），
  // 分頁後僅供粗略參考，不代表全庫今日筆數/登入次數。
  const today = dayjs().format('YYYY-MM-DD');
  const todayCount = items.filter((l) => l.createdAt.startsWith(today)).length;
  // 登入次數：認證登入端點 POST /api/auth/login
  const loginCount = items.filter((l) => l.action.includes('/auth/login')).length;

  /** 軟刪除操作日誌（需 audit-log.delete，僅 admin） */
  const del = useMutation({
    mutationFn: (id: number) => apiClient.delete<{ ok: boolean }>(`/api/audit-log/${id}`),
    onSuccess: () => {
      message.success('日誌已刪除');
      void qc.invalidateQueries({ queryKey: ['settings-audit-log'] });
    },
  });

  const handleViewDetail = (record: AuditLogEntry) => {
    setSelectedLog(record);
    setDetailVisible(true);
  };

  const handleReset = () => {
    setSearchText('');
    setFilterMethod(undefined);
    setDateRange(null);
    resetToFirstPage();
  };

  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: '時間',
      dataIndex: 'createdAt',
      width: 170,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            fontVariantNumeric: 'tabular-nums',
            color: color.text.secondary,
          }}
        >
          {formatDateTime(v)}
        </span>
      ),
    },
    {
      // 序117：新增操作人【姓名】（帳號已刪除等無姓名時回落顯示 ID）
      title: '操作人',
      dataIndex: 'actorName',
      width: 110,
      render: (v: string | null, row) => (
        <span style={{ fontSize: text.sm, color: color.text.secondary }}>
          {v ?? (row.actorId != null ? `#${row.actorId}` : '—')}
        </span>
      ),
    },
    {
      title: '角色',
      dataIndex: 'actorRole',
      width: 90,
      render: (v: string | null) =>
        v ? (
          <StatusPill tone="info" size="sm" variant="outline">
            {v}
          </StatusPill>
        ) : (
          <span style={{ color: color.text.muted }}>—</span>
        ),
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 160,
      render: (v: string) => {
        const { text: label, tone } = formatAuditAction(v);
        // 中文「對象 · 動作」短語；原始 action 字串放 Tooltip 供技術排查
        return (
          <Tooltip title={v}>
            <StatusPill tone={tone} size="sm">
              {label}
            </StatusPill>
          </Tooltip>
        );
      },
    },
    {
      // 序117：操作對象 改名 操作模塊
      title: '操作模塊',
      dataIndex: 'entityType',
      width: 130,
      render: (v: string) => (
        <Tooltip title={`原始類型：${v}`}>
          <StatusPill tone="neutral" size="sm" variant="outline">
            {auditEntityLabel(v)}
          </StatusPill>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="被操作那筆資料的系統編號（資料庫主鍵），例如案件 #42、付款 #41；無對應記錄時顯示「—」。">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            對象編號
            <HelpCircle size={12} color={color.text.muted} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'entityId',
      width: 100,
      render: (v: number | null) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.tertiary }}>
          {v != null ? `#${v}` : '—'}
        </span>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      width: 130,
      render: (v: string | null) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v ?? '—'}
        </span>
      ),
    },
    // 序117/235：操作 改名 操作內容——僅在有人工寫入的 message 時展示細化描述；
    // 沒有 message 時不要重複顯示【操作】欄已有的動作短語（那會讓兩欄看起來一樣），
    // 改用弱化提示區分「有細節」與「無細節」兩種記錄。
    {
      title: '操作內容',
      dataIndex: 'message',
      ellipsis: true,
      render: (v: string | null) =>
        v ? v : <span style={{ color: color.text.muted }}>（無詳情）</span>,
    },
    {
      title: '操作',
      key: '_actions',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="查看詳情">
            <Button
              type="text"
              size="small"
              icon={<Eye size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetail(row);
              }}
            />
          </Tooltip>
          {isAdmin && <EditMessageButton entry={row} />}
          {isAdmin && (
            <Tooltip title="刪除">
              <Button
                type="text"
                size="small"
                danger
                icon={<Trash2 size={12} />}
                onClick={() =>
                  modal.confirm({
                    title: '確定刪除此日誌？',
                    content: '刪除後僅作軟刪除標記，不影響稽核追溯。',
                    okText: '刪除',
                    okButtonProps: { danger: true },
                    onOk: () => del.mutate(row.id),
                  })
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="操作日誌"
        description="記錄並展示系統中所有用戶的操作行為，支援多維度篩選與備注編輯。"
        meta={
          <>
            <PageMetaItem icon={<Activity size={13} />} label="日誌總數" value={`${total} 筆`} />
            <PageMetaItem
              icon={<CalendarDays size={13} />}
              label="今日"
              value={`${todayCount} 筆`}
            />
            <PageMetaItem icon={<LogIn size={13} />} label="登入" value={`${loginCount} 次`} />
            <PageMetaItem icon={<TriangleAlert size={13} />} label="失敗" value={`0 次`} />
          </>
        }
        actions={
          <Button icon={<Download size={14} />} ghost>
            匯出 CSV
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋備注 / 操作對象"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  resetToFirstPage();
                }}
                allowClear
              />
            }
            filters={
              <>
                <FilterBarLabel>日期</FilterBarLabel>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates);
                    resetToFirstPage();
                  }}
                  placeholder={['開始日期', '結束日期']}
                  allowClear
                />
                <FilterBarLabel>操作</FilterBarLabel>
                <Select
                  placeholder="全部動作"
                  value={filterMethod}
                  onChange={(v) => {
                    setFilterMethod(v);
                    resetToFirstPage();
                  }}
                  allowClear
                  style={{ width: 120 }}
                  options={AUDIT_METHOD_OPTIONS}
                />
              </>
            }
            extra={
              (searchText || filterMethod || dateRange) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<AuditLogEntry>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
          scroll={{ x: 1300 }}
          size="middle"
          onRow={(record) => ({
            onClick: () => handleViewDetail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </ListPageShell>

      {/* 日誌詳情 Modal */}
      <Modal
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: space[2] }}>
            <ScrollText size={16} />
            <span>日誌詳情</span>
            {selectedLog && (
              <span
                style={{
                  fontFamily: font.mono,
                  fontSize: text.base,
                  color: color.text.tertiary,
                  fontWeight: weight.medium,
                }}
              >
                #{selectedLog.id}
              </span>
            )}
          </span>
        }
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setSelectedLog(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            關閉
          </Button>,
        ]}
        width={620}
        destroyOnHidden
      >
        {selectedLog && (
          <div style={{ marginTop: space[3] }}>
            <DataFieldGrid columns={2}>
              <DataField label="日誌 ID" value={String(selectedLog.id)} mono />
              <DataField
                label="操作時間"
                value={formatDateTime(selectedLog.createdAt)}
                mono
              />
              <DataField label="操作者 ID" value={String(selectedLog.actorId ?? '—')} mono />
              <DataField label="角色" value={selectedLog.actorRole ?? '—'} />
              <DataField
                label="操作"
                value={
                  <Tooltip title={selectedLog.action}>
                    <StatusPill tone={formatAuditAction(selectedLog.action).tone} size="sm">
                      {formatAuditAction(selectedLog.action).text}
                    </StatusPill>
                  </Tooltip>
                }
              />
              <DataField
                label="操作對象"
                value={
                  <StatusPill tone="neutral" size="sm" variant="outline">
                    {auditEntityLabel(selectedLog.entityType)}
                  </StatusPill>
                }
              />
              <DataField
                label="對象編號"
                value={selectedLog.entityId != null ? `#${selectedLog.entityId}` : '—'}
                mono
              />
              <DataField label="IP 位址" value={selectedLog.ip ?? '—'} mono />
              <DataField label="備注" value={selectedLog.message ?? '—'} span={2} />
            </DataFieldGrid>
          </div>
        )}
      </Modal>
    </>
  );
}

/**
 * 編輯備注按鈕：彈出 Modal 僅允許修改 message 欄位。
 */
function EditMessageButton({ entry }: { entry: AuditLogEntry }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const update = useMutation({
    mutationFn: (v: { message: string }) =>
      apiClient.patch<{ ok: boolean }>(`/api/audit-log/${entry.id}`, v),
    onSuccess: () => {
      message.success('備注已更新');
      void qc.invalidateQueries({ queryKey: ['settings-audit-log'] });
    },
  });

  function handleClick() {
    form.setFieldsValue({ message: entry.message ?? '' });
    modal.confirm({
      title: '編輯備注',
      content: (
        <Form form={form} layout="vertical" className="mt-2">
          <Form.Item name="message" label="備注" rules={[{ max: 2000 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      ),
      okText: '儲存',
      onOk: async () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await update.mutateAsync(await form.validateFields());
      },
    });
  }

  return (
    <Tooltip title="編輯備注">
      <Button type="text" size="small" icon={<Pencil size={12} />} onClick={handleClick} />
    </Tooltip>
  );
}
